import React, { useState } from 'react';
import { Calendar } from '../components/ui/calendar';
import { CheckCircle, Clock, Calendar as CalendarIcon, User, Phone, Mail, Car, ArrowRight, ArrowLeft } from 'lucide-react';
import { serviceDurations, getAvailableSlots } from '../bookingData';
import { services } from '../mockData';
import { toast } from 'sonner';
import { format, addDays, isBefore, startOfToday } from 'date-fns';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingPage = () => {
  const [step, setStep] = useState(1);
  const [selectedService, setSelectedService] = useState('');
  const [selectedPackage, setSelectedPackage] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    vehicleMake: '',
    vehicleModel: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingConfirmation, setBookingConfirmation] = useState(null);

  const today = startOfToday();
  const maxDate = addDays(today, 30);

  // Get service packages based on selected service
  const getServicePackages = () => {
    const service = services.find(s => s.slug === selectedService);
    return service?.packages || [];
  };

  // Get available time slots for selected date
  const availableSlots = selectedDate && selectedPackage
    ? getAvailableSlots(selectedDate, selectedPackage)
    : [];

  const handleServiceChange = (serviceSlug) => {
    setSelectedService(serviceSlug);
    setSelectedPackage('');
    setSelectedDate(null);
    setSelectedTime('');
  };

  const handlePackageChange = (packageSlug) => {
    setSelectedPackage(packageSlug);
    setSelectedDate(null);
    setSelectedTime('');
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime('');
  };

  const handleCustomerInfoChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const bookingData = {
        service: selectedPackage,
        serviceName: serviceDurations[selectedPackage]?.name,
        date: selectedDate.toISOString().split('T')[0],
        time: selectedTime,
        duration: serviceDurations[selectedPackage]?.hours,
        ...customerInfo
      };

      const response = await axios.post(`${API}/bookings`, bookingData);

      if (response.data) {
        setBookingConfirmation({
          ...bookingData,
          bookingId: response.data.id,
          date: selectedDate
        });
        setStep(4);
        toast.success('Your appointment has been successfully booked!');
      }
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to book appointment. Please try again or call us at (647) 326-1174.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetBooking = () => {
    setStep(1);
    setSelectedService('');
    setSelectedPackage('');
    setSelectedDate(null);
    setSelectedTime('');
    setCustomerInfo({
      name: '',
      email: '',
      phone: '',
      vehicleMake: '',
      vehicleModel: '',
      notes: ''
    });
    setBookingConfirmation(null);
  };

  return (
    <div className="booking-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <h1 className="page-title">Book Your Appointment</h1>
          <p className="page-subtitle">
            Schedule your service online in just a few simple steps
          </p>
        </div>
      </section>

      {/* Booking Steps */}
      <section className="booking-section">
        <div className="container">
          {/* Progress Indicator */}
          {step < 4 && (
            <div className="booking-progress">
              <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>
                <div className="step-number">1</div>
                <span>Select Service</span>
              </div>
              <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>
                <div className="step-number">2</div>
                <span>Choose Date & Time</span>
              </div>
              <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>
                <div className="step-number">3</div>
                <span>Your Information</span>
              </div>
            </div>
          )}

          {/* Step 1: Service Selection */}
          {step === 1 && (
            <div className="booking-step">
              <h2 className="step-title">Select Your Service</h2>
              
              <div className="service-selection">
                {services.map((service) => (
                  <div
                    key={service.id}
                    className={`service-option ${selectedService === service.slug ? 'selected' : ''}`}
                    onClick={() => handleServiceChange(service.slug)}
                  >
                    <h3>{service.name}</h3>
                    <p>{service.shortDescription}</p>
                  </div>
                ))}
              </div>

              {selectedService && (
                <div className="package-selection">
                  <h3 className="package-title">Choose Your Package</h3>
                  <div className="packages-grid">
                    {getServicePackages().map((pkg, idx) => {
                      const packageSlug = `${selectedService}-${pkg.name.toLowerCase().replace(/\s+/g, '-')}`;
                      return (
                        <div
                          key={idx}
                          className={`package-option ${selectedPackage === packageSlug ? 'selected' : ''}`}
                          onClick={() => handlePackageChange(packageSlug)}
                        >
                          <h4>{pkg.name}</h4>
                          <p className="package-price">{pkg.price}</p>
                          <p className="package-duration">
                            <Clock size={16} />
                            {pkg.duration}
                          </p>
                          <ul>
                            {pkg.includes.slice(0, 3).map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="step-actions">
                <button
                  className="btn-primary btn-large"
                  disabled={!selectedPackage}
                  onClick={() => setStep(2)}
                >
                  Continue to Date & Time
                  <ArrowRight size={20} />
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Date & Time Selection */}
          {step === 2 && (
            <div className="booking-step">
              <h2 className="step-title">Choose Date & Time</h2>
              
              <div className="selected-service-info">
                <p><strong>Service:</strong> {serviceDurations[selectedPackage]?.name}</p>
                <p><strong>Duration:</strong> Approximately {serviceDurations[selectedPackage]?.hours} hours</p>
              </div>

              <div className="datetime-selection">
                <div className="calendar-container">
                  <h3>Select Date</h3>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={handleDateSelect}
                    disabled={(date) =>
                      isBefore(date, today) || date > maxDate || date.getDay() === 0
                    }
                    className="booking-calendar"
                  />
                  <p className="calendar-note">
                    <CalendarIcon size={16} />
                    You can book up to 30 days in advance
                  </p>
                </div>

                {selectedDate && (
                  <div className="time-slots-container">
                    <h3>Available Time Slots</h3>
                    <p className="selected-date">
                      {format(selectedDate, 'EEEE, MMMM d, yyyy')}
                    </p>
                    
                    <div className="time-slots-grid">
                      {availableSlots.length > 0 ? (
                        availableSlots.map((slot) => (
                          <button
                            key={slot.value}
                            className={`time-slot ${selectedTime === slot.value ? 'selected' : ''}`}
                            onClick={() => setSelectedTime(slot.value)}
                          >
                            <Clock size={18} />
                            {slot.label}
                          </button>
                        ))
                      ) : (
                        <p className="no-slots">
                          No available slots for this date. Please choose another date.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="step-actions">
                <button
                  className="btn-secondary"
                  onClick={() => setStep(1)}
                >
                  <ArrowLeft size={20} />
                  Back
                </button>
                <button
                  className="btn-primary btn-large"
                  disabled={!selectedDate || !selectedTime}
                  onClick={() => setStep(3)}
                >
                  Continue to Your Info
                  <ArrowRight size={20} />
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Customer Information */}
          {step === 3 && (
            <div className="booking-step">
              <h2 className="step-title">Your Information</h2>

              <div className="booking-summary-card">
                <h3>Appointment Summary</h3>
                <div className="summary-details">
                  <p><strong>Service:</strong> {serviceDurations[selectedPackage]?.name}</p>
                  <p><strong>Date:</strong> {format(selectedDate, 'EEEE, MMMM d, yyyy')}</p>
                  <p><strong>Time:</strong> {availableSlots.find(s => s.value === selectedTime)?.label}</p>
                  <p><strong>Duration:</strong> ~{serviceDurations[selectedPackage]?.hours} hours</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="booking-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">
                      <User size={18} />
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={customerInfo.name}
                      onChange={handleCustomerInfoChange}
                      required
                      placeholder="John Smith"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">
                      <Phone size={18} />
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={customerInfo.phone}
                      onChange={handleCustomerInfoChange}
                      required
                      placeholder="(647) 123-4567"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email">
                    <Mail size={18} />
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={customerInfo.email}
                    onChange={handleCustomerInfoChange}
                    required
                    placeholder="john@example.com"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="vehicleMake">
                      <Car size={18} />
                      Vehicle Make
                    </label>
                    <input
                      type="text"
                      id="vehicleMake"
                      name="vehicleMake"
                      value={customerInfo.vehicleMake}
                      onChange={handleCustomerInfoChange}
                      placeholder="e.g., Honda, Toyota, BMW"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="vehicleModel">
                      Vehicle Model
                    </label>
                    <input
                      type="text"
                      id="vehicleModel"
                      name="vehicleModel"
                      value={customerInfo.vehicleModel}
                      onChange={handleCustomerInfoChange}
                      placeholder="e.g., Civic, Camry, X5"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="notes">Additional Notes (Optional)</label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={customerInfo.notes}
                    onChange={handleCustomerInfoChange}
                    rows="3"
                    placeholder="Any special requests or information we should know?"
                  ></textarea>
                </div>

                <div className="step-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setStep(2)}
                  >
                    <ArrowLeft size={20} />
                    Back
                  </button>
                  <button
                    type="submit"
                    className="btn-primary btn-large"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Booking...' : 'Confirm Booking'}
                    <CheckCircle size={20} />
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Step 4: Confirmation */}
          {step === 4 && bookingConfirmation && (
            <div className="booking-step booking-confirmation">
              <div className="confirmation-icon">
                <CheckCircle size={64} />
              </div>
              <h2 className="confirmation-title">Booking Confirmed!</h2>
              <p className="confirmation-message">
                Your appointment has been successfully scheduled. We've sent a confirmation 
                to {bookingConfirmation.email} and a WhatsApp message to {bookingConfirmation.phone}.
              </p>

              <div className="confirmation-details">
                <h3>Appointment Details</h3>
                <div className="detail-item">
                  <strong>Booking ID:</strong> #{bookingConfirmation.bookingId}
                </div>
                <div className="detail-item">
                  <strong>Service:</strong> {bookingConfirmation.serviceName}
                </div>
                <div className="detail-item">
                  <strong>Date:</strong> {format(bookingConfirmation.date, 'EEEE, MMMM d, yyyy')}
                </div>
                <div className="detail-item">
                  <strong>Time:</strong> {availableSlots.find(s => s.value === bookingConfirmation.time)?.label}
                </div>
                <div className="detail-item">
                  <strong>Duration:</strong> Approximately {bookingConfirmation.duration} hours
                </div>
                <div className="detail-item">
                  <strong>Location:</strong> 48 Tamvale Crescent, Kitchener N2E 3K7 ON
                </div>
              </div>

              <div className="confirmation-notes">
                <h4>Important Information:</h4>
                <ul>
                  <li>Please arrive 5-10 minutes early</li>
                  <li>Bring your vehicle and any relevant documentation</li>
                  <li>To reschedule or cancel, please contact us at least 24 hours in advance</li>
                  <li>Call us at (647) 326-1174 if you have any questions</li>
                </ul>
              </div>

              <div className="confirmation-actions">
                <button
                  className="btn-primary btn-large"
                  onClick={resetBooking}
                >
                  Book Another Appointment
                </button>
                <a href="/" className="btn-secondary btn-large">
                  Return to Home
                </a>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BookingPage;
